import { useCallback, useMemo, useState } from 'react';

import { useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  ProductListResponse,
  ProductOptionItem,
  ProductSearchParams,
} from '../types/orderTypes';

export const useProductSearch = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const queryParams = useMemo<ProductSearchParams>(
    () => ({
      page: 1,
      limit: 20,
      keyword: searchKeyword.trim(),
    }),
    [searchKeyword],
  );

  const getProductList = useCallback(
    async (
      params: ProductSearchParams,
    ): Promise<ApiResponse<ProductListResponse>> => {
      const urlSearchParams = new URLSearchParams({
        page: params.page?.toString() || '1',
        limit: params.limit?.toString() || '20',
      });

      if (params.keyword && params.keyword.trim()) {
        urlSearchParams.append('keyword', params.keyword);
      }

      return api.get<ProductListResponse>(
        `/api/v1/product/list?${urlSearchParams.toString()}&filter=all&sort=latest`,
      );
    },
    [],
  );

  const {
    data: productListResponse,
    isLoading,
    error,
  } = useApiQuery(
    ['productSearch', JSON.stringify(queryParams)],
    () => getProductList(queryParams),
    {
      enabled: !!(queryParams.keyword && queryParams.keyword.length > 0),
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const productList: ProductOptionItem[] =
    productListResponse?.data?.productOptionList || [];
  const totalCount = productListResponse?.data?.totalCount || 0;
  const hasResults = productList.length > 0;

  // 검색어 변경 핸들러
  const handleSearchChange = useCallback((value: string) => {
    setSearchKeyword(value);
  }, []);

  return {
    // Data
    productList,
    totalCount,
    isLoading,
    error,
    hasResults,

    // State
    searchKeyword,

    // Actions
    handleSearchChange,
  };
};
